import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import WorkIcon from '@mui/icons-material/Work';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DescriptionIcon from '@mui/icons-material/Description';
import EuroIcon from '@mui/icons-material/Euro';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import HistoryIcon from '@mui/icons-material/History';
import BuildIcon from '@mui/icons-material/Build';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SettingsIcon from '@mui/icons-material/Settings';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SyncIcon from '@mui/icons-material/Sync';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBilling } from '../context/BillingContext';
import { OfflineBanner } from '../components/OfflineBanner';
import { WriteBlockedBanner } from '../components/WriteBlockedBanner';
import { useSnackbar } from '../context/SnackbarContext';
import { useNotifications } from '../context/NotificationContext';
import { useOutbox } from '../outbox/outboxStore';
import { OutboxWorkerBoot } from '../outbox/OutboxWorkerBoot';
import { PushRegistrationBoot } from '../components/PushRegistrationBoot';
import { ClockWidget } from '../components/attendance/ClockWidget';
import { isNative } from '../platform/isNative';
import { AppPreferences } from '../platform/preferences';
import { pushApi } from '../api/push';
import { clearDeliveredNotifications } from '../platform/push';

const DRAWER_WIDTH = 240;

const ROLE_LABELS: Record<string, string> = {
  AMMINISTRATORE_AZIENDA: 'Amministratore',
  RESPONSABILE: 'Responsabile',
  OPERATORE: 'Operatore',
};

interface NavItemLink {
  label: string;
  icon: React.ReactNode;
  path: string;
}
interface NavItemGroup {
  label: string;
  icon: React.ReactNode;
  children: { label: string; path: string }[];
}
type NavItem = NavItemLink | NavItemGroup;

function isNavGroup(item: NavItem): item is NavItemGroup {
  return 'children' in item && Array.isArray((item as NavItemGroup).children);
}

const FINANCE_ALERTS = 'FINANCE_ALERTS';
const PRESENCE = 'PRESENCE';
const DELIVERY_NOTE_SMART_MATCHING = 'DELIVERY_NOTE_SMART_MATCHING';

export function AppShellPage() {
  const { user, logout, billingSnapshot } = useAuth();
  const { isWriteBlocked } = useBilling();
  const hasFinanceAlerts = billingSnapshot?.enabledFeatures?.includes(FINANCE_ALERTS) ?? false;
  const hasPresence = billingSnapshot?.enabledFeatures?.includes(PRESENCE) ?? false;
  const hasDeliveryNoteSmartMatching =
    billingSnapshot?.enabledFeatures?.includes(DELIVERY_NOTE_SMART_MATCHING) ?? false;
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const { unreadCount } = useNotifications();
  const { pendingCount } = useOutbox();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleLogout = async () => {
    setAnchorEl(null);
    try {
      // R8.2: Unregister push device BEFORE clearing auth tokens (while API is still callable).
      if (isNative()) {
        try {
          const deviceId = await AppPreferences.get('cantir.push.deviceId');
          if (deviceId) {
            await pushApi.unregister(deviceId);
          }
          await clearDeliveredNotifications();
        } catch {
          // Push unregistration failure is non-fatal — proceed with logout
        }
      }
      await logout();
      navigate('/login', { replace: true });
    } catch {
      showMessage('Errore durante il logout.', 'error');
    }
  };

  const isOperatore = user?.role === 'OPERATORE';
  const location = useLocation();
  const presenzePathActive = location.pathname.startsWith('/app/attendance');
  const [presenzeOpen, setPresenzeOpen] = useState(presenzePathActive);
  useEffect(() => {
    if (presenzePathActive) setPresenzeOpen(true);
  }, [presenzePathActive]);

  const navItems: NavItem[] = isOperatore
    ? [
        { label: 'Commesse', icon: <WorkIcon />, path: '/app/worksites' },
        // Sezione Presenze nascosta all'Operatore (backend restituirebbe 403 su presenze per commessa).
        {
          label: 'Notifiche',
          icon: (
            <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          ),
          path: '/app/notifications',
        },
      ]
    : [
        { label: 'Dashboard', icon: <DashboardIcon />, path: '/app/dashboard' },
        { label: 'Commesse', icon: <WorkIcon />, path: '/app/worksites' },
        ...(hasPresence
          ? [
              {
                label: 'Presenze',
                icon: <ScheduleIcon />,
                children: [
                  { label: 'Presenze timbratura', path: '/app/attendance' },
                  { label: 'Presenze per commessa', path: '/app/attendance/worksite' },
                ],
              },
            ]
          : [{ label: 'Presenze da rapportino', icon: <ScheduleIcon />, path: '/app/attendance/worksite' }]),
        { label: 'Rapportini', icon: <AssignmentIcon />, path: '/app/reports' },
        { label: 'Documenti', icon: <DescriptionIcon />, path: '/app/documents' },
        { label: 'Clienti', icon: <PersonIcon />, path: '/app/customers' },
        {
          label: 'Notifiche',
          icon: (
            <Badge badgeContent={unreadCount > 0 ? unreadCount : undefined} color="error" max={99}>
              <NotificationsIcon />
            </Badge>
          ),
          path: '/app/notifications',
        },
        ...(user?.role === 'AMMINISTRATORE_AZIENDA'
          ? [{ label: 'Utenti', icon: <PeopleIcon />, path: '/app/users' }]
          : []),
        ...(user?.role === 'OPERATORE'
          ? [
              ...(hasFinanceAlerts
                ? [{ label: 'Dashboard economica', icon: <AssessmentIcon />, path: '/app/finance/dashboard' }]
                : []),
              { label: 'Materiali', icon: <Inventory2Icon />, path: '/app/materials' },
            ]
          : ['AMMINISTRATORE_AZIENDA', 'RESPONSABILE'].includes(user?.role ?? '')
            ? [
                ...(hasFinanceAlerts
                  ? [
                      { label: 'Costo orario', icon: <EuroIcon />, path: '/app/finance/user-costs' },
                      { label: 'Alert finanziari', icon: <WarningAmberIcon />, path: '/app/finance/alerts' },
                      { label: 'Dashboard economica', icon: <AssessmentIcon />, path: '/app/finance/dashboard' },
                    ]
                  : []),
                { label: 'Promemoria cliente', icon: <CampaignIcon />, path: '/app/reminders/templates' },
                { label: 'Registro audit', icon: <HistoryIcon />, path: '/app/audit' },
                { label: 'Attrezzature', icon: <BuildIcon />, path: '/app/equipment' },
                { label: 'Materiali', icon: <Inventory2Icon />, path: '/app/materials' },
                { label: 'Materiali da revisionare', icon: <Inventory2Icon />, path: '/app/materials/drafts' },
                ...(hasDeliveryNoteSmartMatching
                  ? [{ label: 'Mapping fornitore–materiale', icon: <Inventory2Icon />, path: '/app/supplier-material-mappings' }]
                  : []),
                { label: 'Configurazione magazzino', icon: <SettingsIcon />, path: '/app/config/warehouse' },
              ]
            : []),
      ];


  const linkSx = {
    borderRadius: 2,
    mx: 1,
    mb: 0.5,
    '&.active': {
      bgcolor: 'primary.main',
      color: 'white',
      '& .MuiListItemIcon-root': { color: 'white' },
    },
  };

  const childLinkSx = {
    ...linkSx,
    pl: 5,
    py: 0.75,
    borderLeft: '2px solid',
    borderColor: 'divider',
    ml: 2,
    mr: 1,
    mb: 0.5,
  };

  const presenzeChildIcons: Record<string, React.ReactNode> = {
    '/app/attendance': <AccessTimeIcon fontSize="small" />,
    '/app/attendance/worksite': <WorkIcon fontSize="small" />,
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar>
        <Typography variant="h6" fontWeight={800} color="primary">
          Cantir
        </Typography>
      </Toolbar>
      <Divider />
      <List sx={{ flex: 1 }}>
        {navItems.map((item) => {
          if (isNavGroup(item)) {
            const key = `group-${item.label}`;
            const isOpen = item.label === 'Presenze' ? presenzeOpen : false;
            const setOpen = item.label === 'Presenze' ? setPresenzeOpen : undefined;
            return (
              <Box
                key={key}
                sx={{
                  mx: 1,
                  mb: 0.5,
                  borderRadius: 2,
                  bgcolor: isOpen ? 'action.hover' : 'transparent',
                  transition: 'background-color 0.2s ease',
                }}
              >
                <ListItemButton
                  onClick={() => setOpen?.((prev) => !prev)}
                  sx={{
                    borderRadius: 2,
                    '&:hover': { bgcolor: isOpen ? 'action.selected' : 'action.hover' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      variant: 'body2',
                      fontWeight: 600,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  />
                  {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pt: 0, pb: 0.5 }}>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.path}
                        component={NavLink}
                        to={child.path}
                        end={child.path === '/app/attendance'}
                        onClick={() => setMobileOpen(false)}
                        sx={childLinkSx}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {presenzeChildIcons[child.path]}
                        </ListItemIcon>
                        <ListItemText primary={child.label} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          }
          return (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={linkSx}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          );
        })}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      {/* Invisible component: boots the outbox worker & listens for network events */}
      <OutboxWorkerBoot />
      {/* R8.2: boots push registration + listeners on native platforms */}
      <PushRegistrationBoot />

      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={1}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Cantir
          </Typography>

          <Chip
            label={ROLE_LABELS[user?.role ?? ''] ?? user?.role}
            size="small"
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', mr: 1 }}
            variant="outlined"
          />

          {/* Sync / Outbox icon — shows pending count badge */}
          <Tooltip title={pendingCount > 0 ? `${pendingCount} operazioni in coda` : 'Sincronizzazione'}>
            <IconButton
              color="inherit"
              onClick={() => navigate('/app/outbox')}
              aria-label="coda sincronizzazione"
              sx={{ mr: 0.5 }}
            >
              <Badge
                badgeContent={pendingCount > 0 ? pendingCount : undefined}
                color="warning"
                max={99}
              >
                <SyncIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: 14 }}>
              {user?.userId?.slice(0, 2).toUpperCase() ?? 'CA'}
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem dense disabled>
              <Typography variant="caption" color="text.secondary">
                {ROLE_LABELS[user?.role ?? ''] ?? user?.role}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/app/profile');
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              Profilo
            </MenuItem>
            {/* R8.2: Notification settings — primarily useful on native */}
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/app/settings/notifications');
              }}
            >
              <ListItemIcon>
                <NotificationsActiveIcon fontSize="small" />
              </ListItemIcon>
              Impostazioni notifiche
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
        open
      >
        {drawerContent}
      </Drawer>

      {/* Page content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px',
          minHeight: 'calc(100dvh - 64px)',
          bgcolor: 'background.default',
        }}
      >
        <OfflineBanner />
        <WriteBlockedBanner visible={isWriteBlocked} />
        <Outlet />
        {hasPresence && <ClockWidget />}
      </Box>
    </Box>
  );
}
